import React, { useEffect, useState } from "react";
import {
  Copy, Check, Eye, EyeOff, Webhook, RefreshCw, Trash2, AlertCircle,
  Code2, Save, RotateCcw, MapPin, Pencil, Database, CheckCircle2, XCircle, Clock,
} from "lucide-react";
import http from "@/lib/http";
import { t } from "@/i18n";
import { useAuth } from "@/lib/auth";

const TEMPLATE_KINDS = ["makelaar", "particulier"];
const TEMPLATE_TAB_KEY = {
  makelaar: "settings.tab_makelaar",
  particulier: "settings.tab_particulier",
};
const TEMPLATE_HINT_KEY = {
  makelaar: "settings.paste_hint_makelaar",
  particulier: "settings.paste_hint_particulier",
};
const TEMPLATE_FILENAME = {
  makelaar: "flowzy_makelaar.js",
  particulier: "flowzy_particulier.js",
};

export default function Settings() {
  const { user } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reveal, setReveal] = useState({});
  const [copied, setCopied] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [templatesTab, setTemplatesTab] = useState("makelaar");
  const [newlyRotated, setNewlyRotated] = useState(null);
  const [rotating, setRotating] = useState(false);

  // Webhook URL config
  const [urlConfig, setUrlConfig] = useState(null);
  const [urlDraft, setUrlDraft] = useState("");
  const [savingUrl, setSavingUrl] = useState(false);
  const [urlError, setUrlError] = useState(null);
  const [urlSaved, setUrlSaved] = useState(false);

  // Postcode admin
  const [tenantUsers, setTenantUsers] = useState([]);
  const [editingPostcodes, setEditingPostcodes] = useState(null); // user_id being edited
  const [postcodesDraft, setPostcodesDraft] = useState("");
  const [savingPostcodes, setSavingPostcodes] = useState(false);
  const [postcodesSaved, setPostcodesSaved] = useState(null);

  const isAdmin = user?.role === "super_admin" || user?.role === "tenant_admin";

  const loadAll = async () => {
    setLoading(true);
    try {
      const [k, tpl, urlR, usersR] = await Promise.all([
        http.get("/api/settings/api-keys"),
        http.get("/api/settings/flowzy-templates"),
        http.get("/api/settings/webhook-url"),
        http.get("/api/users"),
      ]);
      setKeys(k.data);
      setTemplates(tpl.data);
      setUrlConfig(urlR.data);
      setUrlDraft(urlR.data.full_url || "");
      setTenantUsers(usersR.data);
    } catch {
      setKeys([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (isAdmin) loadAll();
    else setLoading(false);
  }, [isAdmin]);

  const copy = async (what, val) => {
    await navigator.clipboard.writeText(val);
    setCopied(what);
    setTimeout(() => setCopied(null), 1500);
  };

  const mask = (k) => k.slice(0, 10) + "…" + k.slice(-6);

  const rotate = async () => {
    if (!window.confirm(t("settings.rotate_confirm"))) return;
    setRotating(true);
    try {
      const r = await http.post("/api/settings/api-keys/rotate");
      setNewlyRotated(r.data);
      setReveal(prev => ({ ...prev, [r.data.id]: true }));
      await loadAll();
    } finally { setRotating(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm(t("settings.revoke_confirm"))) return;
    await http.post(`/api/settings/api-keys/${id}/revoke`);
    await loadAll();
  };

  const saveUrl = async () => {
    setUrlError(null);
    const v = urlDraft.trim();
    if (v && !/^https?:\/\//i.test(v)) {
      setUrlError(t("settings.endpoint_invalid"));
      return;
    }
    setSavingUrl(true);
    try {
      await http.put("/api/settings/webhook-url", { url: v });
      await loadAll();
      setUrlSaved(true);
      setTimeout(() => setUrlSaved(false), 2000);
    } catch (e) {
      setUrlError(e.response?.data?.detail || t("common.error_generic"));
    } finally { setSavingUrl(false); }
  };

  const resetUrl = async () => {
    setSavingUrl(true);
    try {
      await http.put("/api/settings/webhook-url", { url: "" });
      await loadAll();
    } finally { setSavingUrl(false); }
  };

  const startEditPostcodes = (u) => {
    setEditingPostcodes(u.id);
    setPostcodesDraft((u.postcodes || []).join(", "));
  };

  const savePostcodes = async (userId) => {
    setSavingPostcodes(true);
    try {
      const list = postcodesDraft
        .split(/[,\s;]+/)
        .map(s => s.trim())
        .filter(Boolean);
      await http.put(`/api/users/${userId}/postcodes`, { postcodes: list });
      await loadAll();
      setEditingPostcodes(null);
      setPostcodesSaved(userId);
      setTimeout(() => setPostcodesSaved(null), 2000);
    } finally { setSavingPostcodes(false); }
  };

  // Detect overlapping postcodes (assigned to >1 user)
  const overlappingPostcodes = (() => {
    const counts = {};
    tenantUsers.forEach(u => (u.postcodes || []).forEach(p => {
      counts[p] = (counts[p] || 0) + 1;
    }));
    return Object.entries(counts).filter(([, n]) => n > 1).map(([p]) => p);
  })();

  const currentTemplate = templates?.[templatesTab];
  const urlChanged = urlConfig && urlDraft.trim() !== (urlConfig.full_url || "").trim();

  return (
    <div className="p-8 max-w-[1000px] mx-auto fade-in" data-testid="settings-screen">
      <div className="mb-6">
        <div className="text-[13px] text-[var(--teal-700)] font-medium mb-2 uppercase tracking-[0.14em]">
          {t("nav.settings")}
        </div>
        <h1 className="font-display text-[32px] leading-tight text-[var(--ink-900)]">{t("settings.title")}</h1>
        <p className="text-[14px] text-[var(--ink-500)] mt-1">{t("settings.sub")}</p>
      </div>

      {/* Profile */}
      <section className="mb-8">
        <h2 className="font-display text-[18px] text-[var(--ink-900)] mb-3">{t("settings.profile")}</h2>
        <div className="card">
          <dl className="grid grid-cols-2 gap-4 text-[13px]">
            <div><dt className="text-[11px] uppercase tracking-wider text-[var(--ink-500)]">Naam</dt>
                 <dd className="font-semibold text-[var(--ink-900)]">{user?.name}</dd></div>
            <div><dt className="text-[11px] uppercase tracking-wider text-[var(--ink-500)]">{t("auth.email")}</dt>
                 <dd className="font-semibold text-[var(--ink-900)]">{user?.email}</dd></div>
            <div><dt className="text-[11px] uppercase tracking-wider text-[var(--ink-500)]">Rol</dt>
                 <dd className="font-semibold text-[var(--ink-900)]">{t(`roles.${user?.role}`)}</dd></div>
            <div><dt className="text-[11px] uppercase tracking-wider text-[var(--ink-500)]">Tenant ID</dt>
                 <dd className="font-mono text-[12px] text-[var(--ink-700)]">{user?.tenant_id}</dd></div>
          </dl>
        </div>
      </section>

      {/* Postcode-toewijzing — admin */}
      {isAdmin && (
        <MondoSyncPanel />
      )}

      {/* Postcode-toewijzing — admin */}
      {isAdmin && (
        <section className="mb-8" data-testid="postcodes-section">
          <div className="flex items-center gap-2 mb-3">
            <MapPin size={18} className="text-[var(--teal-700)]" />
            <h2 className="font-display text-[18px] text-[var(--ink-900)]">{t("settings.postcodes_section")}</h2>
          </div>
          <p className="text-[13px] text-[var(--ink-500)] mb-4">{t("settings.postcodes_section_sub")}</p>

          {overlappingPostcodes.length > 0 && (
            <div className="mb-3 p-3 rounded-lg bg-[#FDF3DF] border border-[#EAD49B] flex items-start gap-3"
                 data-testid="postcode-overlap-warning">
              <AlertCircle size={15} className="text-[#8A5D10] mt-0.5 shrink-0" />
              <div className="text-[12px] text-[#5A3F0B]">
                {t("settings.postcodes_overlap_warning", { count: overlappingPostcodes.length })}
                {" "}<span className="font-mono">{overlappingPostcodes.join(", ")}</span>
              </div>
            </div>
          )}

          <ul className="space-y-2" data-testid="postcodes-user-list">
            {tenantUsers.map(u => (
              <li key={u.id} className="card !p-4 flex items-center gap-4" data-testid={`postcodes-user-${u.id}`}>
                <div className="w-9 h-9 rounded-full bg-[var(--teal-100)] text-[var(--teal-700)] grid place-items-center text-[11px] font-semibold shrink-0">
                  {u.name.split(" ").map(p => p[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold text-[var(--ink-900)] truncate">{u.name}</div>
                  <div className="text-[11px] text-[var(--ink-500)] mb-2">{t(`roles.${u.role}`)}</div>
                  {editingPostcodes === u.id ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={postcodesDraft}
                        onChange={(e) => setPostcodesDraft(e.target.value)}
                        placeholder={t("settings.postcodes_placeholder")}
                        data-testid={`postcodes-input-${u.id}`}
                        className="flex-1 h-9 px-3 rounded-lg border border-[var(--border-soft)] bg-white font-mono text-[12.5px] outline-none focus:border-[var(--teal-500)]"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") savePostcodes(u.id);
                          if (e.key === "Escape") setEditingPostcodes(null);
                        }}
                      />
                      <button type="button" onClick={() => savePostcodes(u.id)} disabled={savingPostcodes}
                              data-testid={`postcodes-save-${u.id}`}
                              className="btn btn-primary !px-3 h-9 text-[12px] disabled:opacity-50">
                        <Check size={13} /> {t("settings.postcodes_save")}
                      </button>
                      <button type="button" onClick={() => setEditingPostcodes(null)}
                              data-testid={`postcodes-cancel-${u.id}`}
                              className="btn btn-ghost !px-3 h-9 text-[12px]">
                        {t("common.cancel")}
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {(u.postcodes || []).length === 0 ? (
                        <span className="text-[12px] text-[var(--ink-400)] italic">{t("settings.no_postcodes")}</span>
                      ) : (
                        (u.postcodes || []).map(p => (
                          <span key={p} className={`chip ${overlappingPostcodes.includes(p) ? "chip-warn" : "chip-teal"} !text-[11px] tabular`}>
                            {p}
                          </span>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {editingPostcodes !== u.id && (
                  <button type="button" onClick={() => startEditPostcodes(u)}
                          data-testid={`postcodes-edit-${u.id}`}
                          className="btn btn-ghost !px-3 h-9 text-[12px] shrink-0">
                    {postcodesSaved === u.id
                      ? <><Check size={13} className="text-[var(--success)]" /> {t("settings.postcodes_saved")}</>
                      : <><Pencil size={13} /> {t("leads.platforms.edit")}</>}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Webhook */}
      {isAdmin && (
        <section data-testid="webhook-section" className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Webhook size={18} className="text-[var(--teal-700)]" />
            <h2 className="font-display text-[18px] text-[var(--ink-900)]">{t("settings.webhook")}</h2>
          </div>
          <p className="text-[13px] text-[var(--ink-500)] mb-4">{t("settings.webhook_sub")}</p>

          <div className="card space-y-5">
            <div>
              <span className="text-[11px] uppercase tracking-wider text-[var(--ink-500)]">
                {t("settings.method")}
              </span>
              <div className="mt-1 font-mono text-[13px] text-[var(--ink-900)]">POST</div>
            </div>

            {/* Editable Endpoint URL */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] uppercase tracking-wider text-[var(--ink-500)]">
                  {t("settings.endpoint")}
                </span>
                <button type="button"
                        onClick={() => copy("endpoint", urlConfig?.full_url || "")}
                        className="text-[11px] text-[var(--teal-600)] hover:underline flex items-center gap-1">
                  {copied === "endpoint"
                    ? <><Check size={12} /> Gekopieerd</>
                    : <><Copy size={12} /> Kopiëren</>}
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={urlDraft}
                  onChange={(e) => { setUrlDraft(e.target.value); setUrlError(null); }}
                  placeholder="https://jouw-productie-domein.be/api/v1/webhooks/flowzy/leads"
                  data-testid="webhook-url-input"
                  className="flex-1 h-10 px-3 rounded-lg border border-[var(--border-soft)] bg-white font-mono text-[12.5px] text-[var(--ink-900)] outline-none focus:border-[var(--teal-500)]"
                />
                {urlChanged && (
                  <button type="button" onClick={saveUrl} disabled={savingUrl}
                          data-testid="webhook-url-save"
                          className="btn btn-primary !px-4 h-10 text-[13px] disabled:opacity-50">
                    {urlSaved
                      ? <><Check size={14} /> {t("settings.endpoint_saved")}</>
                      : <><Save size={14} /> {t("settings.endpoint_save")}</>}
                  </button>
                )}
                {urlConfig?.is_custom && !urlChanged && (
                  <button type="button" onClick={resetUrl} disabled={savingUrl}
                          data-testid="webhook-url-reset"
                          className="btn btn-ghost !px-3 h-10 text-[12px]">
                    <RotateCcw size={13} /> {t("settings.endpoint_reset")}
                  </button>
                )}
              </div>
              {urlError && <div className="text-[12px] text-[var(--danger)] mt-1.5">{urlError}</div>}
              <div className="text-[11px] text-[var(--ink-500)] mt-1.5">{t("settings.endpoint_hint")}</div>
            </div>

            <div>
              <span className="text-[11px] uppercase tracking-wider text-[var(--ink-500)]">
                {t("settings.idempotency")}
              </span>
              <div className="mt-1 font-mono text-[13px] text-[var(--ink-900)]">X-Idempotency-Key</div>
              <div className="text-[11px] text-[var(--ink-500)] mt-1">{t("settings.idempotency_hint")}</div>
            </div>

            {/* API keys */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider text-[var(--ink-500)]">
                  {t("settings.api_key")}
                </div>
                <button type="button" onClick={rotate} disabled={rotating}
                        data-testid="api-key-rotate-btn"
                        className="btn btn-ghost !px-3 !py-1.5 h-8 text-[12px]">
                  <RefreshCw size={12} className={rotating ? "animate-spin" : ""} /> {t("settings.rotate_key")}
                </button>
              </div>
              {loading ? (
                <div className="text-sm text-[var(--ink-500)]">{t("common.loading")}</div>
              ) : keys.length === 0 ? (
                <div className="text-sm text-[var(--ink-500)]">Geen API keys.</div>
              ) : (
                <ul className="space-y-2" data-testid="api-keys-list">
                  {keys.map(k => (
                    <li key={k.id} className="flex items-center gap-3 bg-[var(--sand-50)] border border-[var(--border-soft)] rounded-lg p-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[12px] text-[var(--ink-700)] font-medium">{k.label}</span>
                          <span className={`chip !py-[1px] !px-2 !text-[10px]
                            ${k.status === "active" ? "chip-success" : "chip-warn"}`}
                                data-testid={`api-key-status-${k.id}`}>
                            {k.status === "active" ? t("settings.status_active") : t("settings.status_grace")}
                          </span>
                        </div>
                        <div className="font-mono text-[13px] text-[var(--ink-900)] truncate"
                             data-testid={`api-key-${k.id}`}>
                          {reveal[k.id] ? k.key : mask(k.key)}
                        </div>
                        <div className="flex gap-4 text-[11px] text-[var(--ink-400)] mt-1 tabular">
                          <span>{t("settings.used_24h")}: {k.usage?.last_24h ?? 0}</span>
                          <span>{t("settings.used_7d")}: {k.usage?.last_7d ?? 0}</span>
                          <span>{t("settings.used_30d")}: {k.usage?.last_30d ?? 0}</span>
                          <span>{t("settings.last_used")}: {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString("nl-BE") : t("settings.never")}</span>
                        </div>
                      </div>
                      <button type="button" onClick={() => setReveal(r => ({ ...r, [k.id]: !r[k.id] }))}
                              data-testid={`api-key-reveal-${k.id}`}
                              className="p-2 rounded-lg hover:bg-white text-[var(--ink-500)]">
                        {reveal[k.id] ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                      <button type="button" onClick={() => copy(`key-${k.id}`, k.key)}
                              data-testid={`api-key-copy-${k.id}`}
                              className="p-2 rounded-lg hover:bg-white text-[var(--ink-500)]">
                        {copied === `key-${k.id}` ? <Check size={15} className="text-[var(--success)]" /> : <Copy size={15} />}
                      </button>
                      <button type="button" onClick={() => revoke(k.id)}
                              data-testid={`api-key-revoke-${k.id}`}
                              className="p-2 rounded-lg hover:bg-white text-[var(--ink-500)] hover:text-[var(--danger)]">
                        <Trash2 size={15} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {newlyRotated && (
                <div className="mt-3 p-3 rounded-lg bg-[var(--teal-50)] border border-[var(--teal-100)] flex items-start gap-3"
                     data-testid="rotate-notice">
                  <AlertCircle size={15} className="text-[var(--teal-700)] mt-0.5 shrink-0" />
                  <div className="text-[12px] text-[var(--teal-700)] flex-1">
                    <div className="font-semibold">Nieuwe API key gegenereerd</div>
                    <div className="mt-0.5">
                      De oude key blijft 24u geldig (grace). De JS-code hieronder gebruikt automatisch de nieuwe key.
                    </div>
                  </div>
                  <button type="button" onClick={() => setNewlyRotated(null)}
                          className="text-[var(--teal-700)] text-[11px] hover:underline">OK</button>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Flowzy JS code generator — read-only, 2 tabs */}
      {isAdmin && templates && (
        <section data-testid="flowzy-templates-section">
          <div className="flex items-center gap-2 mb-3">
            <Code2 size={18} className="text-[var(--teal-700)]" />
            <h2 className="font-display text-[18px] text-[var(--ink-900)]">{t("settings.js_templates")}</h2>
          </div>
          <p className="text-[13px] text-[var(--ink-500)] mb-4">{t("settings.js_templates_sub")}</p>

          {/* Tabs */}
          <div className="flex gap-1 mb-3 border-b border-[var(--border-soft)]" data-testid="flowzy-tabs">
            {TEMPLATE_KINDS.map(kind => (
              <button key={kind} type="button" data-testid={`flowzy-tab-${kind}`}
                      onClick={() => setTemplatesTab(kind)}
                      className={`px-4 py-2 text-[13px] font-medium border-b-2 -mb-px transition
                        ${templatesTab === kind
                          ? "text-[var(--teal-700)] border-[var(--teal-600)]"
                          : "text-[var(--ink-500)] border-transparent hover:text-[var(--ink-900)]"}`}>
                {t(TEMPLATE_TAB_KEY[kind])}
              </button>
            ))}
          </div>

          <div className="card !p-0 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--border-soft)] bg-[var(--sand-50)]">
              <div className="text-[11px] text-[var(--ink-500)] font-mono">
                {TEMPLATE_FILENAME[templatesTab]}
              </div>
              <button type="button"
                      data-testid={`flowzy-copy-${templatesTab}`}
                      onClick={() => copy(`tpl-${templatesTab}`, currentTemplate?.content || "")}
                      className="btn btn-ghost !px-3 !py-1 h-8 text-[12px]">
                {copied === `tpl-${templatesTab}`
                  ? <><Check size={12} /> {t("settings.copied")}</>
                  : <><Copy size={12} /> {t("settings.copy_code")}</>}
              </button>
            </div>
            <pre className="bg-[var(--ink-900)] text-[#D5EEF0] text-[12px] p-4 overflow-auto font-mono leading-relaxed max-h-[520px] m-0"
                 data-testid={`flowzy-code-${templatesTab}`}>
              {currentTemplate?.content}
            </pre>
          </div>
          <p className="text-[12px] text-[var(--ink-500)] mt-3">
            {t(TEMPLATE_HINT_KEY[templatesTab])}
          </p>
        </section>
      )}
    </div>
  );
}


function MondoSyncPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const r = await http.get("/api/mondo/status");
      setStatus(r.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Status kon niet worden opgehaald");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  // Auto-poll while a run is in progress
  useEffect(() => {
    if (!status?.runs?.length) return;
    const top = status.runs[0];
    if (top?.status !== "running") return;
    const id = setInterval(load, 2500);
    return () => clearInterval(id);
  }, [status]);

  const trigger = async (mode) => {
    if (mode === "backfill" &&
        !window.confirm("Volledige backfill opnieuw uitvoeren? Dit kan even duren bij veel postcodes.")) {
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await http.post("/api/mondo/sync", { mode });
      // Brief delay so the new "running" run appears in the list
      setTimeout(load, 800);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Sync kon niet gestart worden");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <section className="mb-8" data-testid="mondo-sync-section">
        <div className="text-[13px] text-[var(--ink-500)]">Mondo status laden…</div>
      </section>
    );
  }

  const top = status?.runs?.[0];
  const running = top?.status === "running";
  const ok = status?.last_status === "ok";
  const failed = status?.last_status === "error";

  const statusBadge = running
    ? { cls: "bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]", label: "Loopt nu", icon: <Clock size={12} className="animate-spin-slow" /> }
    : ok
      ? { cls: "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]", label: "Gezond", icon: <CheckCircle2 size={12} /> }
      : failed
        ? { cls: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]", label: "Fout", icon: <XCircle size={12} /> }
        : { cls: "bg-[var(--sand-100)] text-[var(--ink-500)] border-[var(--border-soft)]", label: "Nog niet uitgevoerd", icon: <Clock size={12} /> };

  return (
    <section className="mb-8" data-testid="mondo-sync-section">
      <div className="flex items-center gap-2 mb-3">
        <Database size={18} className="text-[var(--teal-700)]" />
        <h2 className="font-display text-[18px] text-[var(--ink-900)]">Mondo synchronisatie</h2>
        <span className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${statusBadge.cls}`}
              data-testid="mondo-status-badge">
          {statusBadge.icon} {statusBadge.label}
        </span>
      </div>
      <p className="text-[13px] text-[var(--ink-500)] mb-4">
        Dagelijks om {String(status.scheduled_hour_brussels).padStart(2, "0")}:00 (Brussel) haalt de app de Mondo
        Vastgoed-database op en werkt actieve panden bij. Panden zonder link worden offline gemarkeerd.
      </p>

      {err && (
        <div className="mb-3 p-3 rounded-lg bg-[#FEE2E2] border border-[#FECACA] text-[12px] text-[#991B1B]"
             data-testid="mondo-error">
          {err}
        </div>
      )}

      <div className="card !p-5">
        {/* Headline metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <Stat label="Mondo-leads" value={status.mondo_leads_count} testid="mondo-leads-count" />
          <Stat label="Postcodes Makelaar" value={status.postcodes_makelaar_count} />
          <Stat label="Postcodes Particulier" value={status.postcodes_particulier_count} />
          <Stat label="Backfill" value={status.backfill_done ? "Voltooid" : "Niet uitgevoerd"} />
        </div>

        <div className="text-[12px] text-[var(--ink-500)] mb-4 flex flex-wrap gap-x-6 gap-y-1">
          <span>Laatste run: <strong className="text-[var(--ink-900)] tabular">{fmtDt(status.last_run_at) || "—"}</strong></span>
          <span>Laatste source-update gezien: <strong className="text-[var(--ink-900)] tabular">{fmtDt(status.cursor_at) || "—"}</strong></span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button type="button"
                  onClick={() => trigger("manual")}
                  disabled={busy || running}
                  data-testid="mondo-sync-now"
                  className="btn btn-primary !h-9 !px-4 !text-[13px] disabled:opacity-50 disabled:cursor-not-allowed">
            <RefreshCw size={13} className={running ? "animate-spin" : ""} />
            {running ? "Bezig…" : "Nu syncen"}
          </button>
          <button type="button"
                  onClick={() => trigger("backfill")}
                  disabled={busy || running}
                  data-testid="mondo-backfill"
                  className="btn btn-ghost !h-9 !px-3 !text-[12px] disabled:opacity-50 disabled:cursor-not-allowed">
            Volledige backfill
          </button>
          <button type="button"
                  onClick={load}
                  data-testid="mondo-refresh"
                  className="btn btn-ghost !h-9 !px-3 !text-[12px]">
            <RotateCcw size={12} /> Vernieuwen
          </button>
        </div>
      </div>

      {/* Run history */}
      {status.runs?.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wider text-[var(--ink-500)] mb-2">Recente runs</div>
          <div className="card !p-0 overflow-hidden">
            <table className="w-full text-[12px]" data-testid="mondo-runs-table">
              <thead className="bg-[var(--sand-50)] text-[var(--ink-500)] uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="text-left px-4 py-2">Tijd</th>
                  <th className="text-left px-3 py-2">Modus</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Gescand</th>
                  <th className="text-right px-3 py-2">Nieuw</th>
                  <th className="text-right px-3 py-2">Update</th>
                  <th className="text-right px-3 py-2">Offline</th>
                  <th className="text-right px-3 py-2">Terug online</th>
                  <th className="text-right px-4 py-2">Duur</th>
                </tr>
              </thead>
              <tbody>
                {status.runs.map(r => (
                  <tr key={r.id} className="border-t border-[var(--border-soft)]"
                      data-testid={`mondo-run-${r.id}`}>
                    <td className="px-4 py-2 tabular text-[var(--ink-700)]">{fmtDt(r.started_at)}</td>
                    <td className="px-3 py-2 capitalize">{r.mode}</td>
                    <td className="px-3 py-2">
                      <RunStatusChip status={r.status} />
                    </td>
                    <td className="px-3 py-2 tabular text-right">{r.rows_scanned}</td>
                    <td className="px-3 py-2 tabular text-right text-[var(--teal-700)] font-medium">{r.leads_added || 0}</td>
                    <td className="px-3 py-2 tabular text-right">{r.leads_updated || 0}</td>
                    <td className="px-3 py-2 tabular text-right text-[#B43232]">{r.leads_marked_offline || 0}</td>
                    <td className="px-3 py-2 tabular text-right text-[#3B8C5C]">{r.leads_back_online || 0}</td>
                    <td className="px-4 py-2 tabular text-right text-[var(--ink-500)]">
                      {r.duration_ms != null ? `${(r.duration_ms / 1000).toFixed(1)}s` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Surface error from last failed run */}
          {top?.status === "error" && top?.error && (
            <div className="mt-3 p-3 rounded-lg bg-[#FEE2E2] border border-[#FECACA] text-[12px] text-[#991B1B] font-mono"
                 data-testid="mondo-last-error">
              {top.error}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-[var(--ink-500)]">{label}</div>
      <div className="text-[20px] font-display text-[var(--ink-900)] tabular mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

function RunStatusChip({ status }) {
  if (status === "running") {
    return <span className="inline-flex items-center gap-1 chip-blue px-2 py-0.5 rounded-full text-[10px] font-medium border bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]"><Clock size={10} className="animate-spin" /> Bezig</span>;
  }
  if (status === "ok") {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]"><Check size={10} /> OK</span>;
  }
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]"><XCircle size={10} /> Fout</span>;
}

function fmtDt(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("nl-BE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
